import React, {useContext, useEffect, useState} from "react";
import {createUseStyles} from "react-jss";
import {useImmer} from "use-immer";
import {EngageMessageType} from "engage-common/messages";
import {EngageAppContext} from "../../App";
import Button from "engage-client/components/Button";
import {validateAndPullID} from "../common/HostBubble";
import {CommonState, GameOptions, TriviaStatus} from "../../state/state";
import {TriviaAction, TriviaMessage} from "../../messages/messages";
import {getInputValue} from "engage-client/utils";
import TextField from "engage-client/components/TextField";
import Switch from "engage-client/components/Switch";
import {TextVariant} from "engage-client/components/DisplayText";

const useStyles = createUseStyles({
    optionContainer: {
        display:"flex",
        flexDirection:"column",
        "& > div": {
            margin: [8,0]
        }
    },
    buttonContainer: {
        display: "flex",
        flexDirection: "row",
    },
    button: {
        margin: 8
    }
});

export default function HostOptions() {
    const {sendMessage, lobby, appState} = useContext(EngageAppContext.current);
    const classes = useStyles();

    function killLobby() {
        if(lobby) {
            if(window.confirm("Do you really want to kill this lobby? There's no going back!")) {
                sendMessage({
                    type: EngageMessageType.KillLobby,
                    lobbyID: lobby.id
                }, () => {
                    window.location.reload();
                });
            }
        }
    }

    function getHostCode() {
        if(lobby) {
            sendMessage({
                type: EngageMessageType.GetHostCode,
                lobbyID: lobby.id
            }, (hostCode:string) => {
                alert(`Host code is: ${hostCode}`);
            });
        }
    }

    function openSharedDisplay() {
        window.open("/shared", "_blank");
    }

    function downloadResults() {
        if(lobby) {
            sendMessage({
                type: TriviaAction.DownloadResults
            }, (summary:string, responses:string, teams:string, chat:string) => {
                triggerDownload(summary, `trivia-summary-${lobby.id}.csv`);
                triggerDownload(responses, `trivia-responses-${lobby.id}.csv`);
                if(teams) {
                    triggerDownload(teams, `trivia-teams-${lobby.id}.csv`);
                }
                if(chat) {
                    triggerDownload(chat, `trivia-chat-${lobby.id}.csv`);
                }
            });
        }
    }

    function triggerDownload(content:string, filename:string) {
        const dataStr = "data:text/csv;charset=utf-8," + content;
        let downloadAnchorNode = document.createElement("a");
        downloadAnchorNode.setAttribute("href",     dataStr);
        downloadAnchorNode.setAttribute("download", filename);
        document.body.appendChild(downloadAnchorNode); // required for firefox
        downloadAnchorNode.click();
        downloadAnchorNode.remove();
    }

    return (
        <>
            <div className={classes.optionContainer}>
                <GameSettings common={appState.common} sendMessage={sendMessage}/>
                <div className={classes.buttonContainer}>
                    <Button onClick={openSharedDisplay} className={classes.button}>Launch Shared Display</Button>
                    <Button onClick={downloadResults} className={classes.button}
                            disabled={appState.common.state !== TriviaStatus.Complete}>Download Results</Button>
                </div>
                <div className={classes.buttonContainer}>
                    <Button onClick={getHostCode} className={classes.button}>Get Host Code</Button>
                    <Button onClick={killLobby} className={classes.button} backgroundColor="secondary">Kill Lobby</Button>
                </div>

            </div>
        </>
    );
};

const isYoutubeURLValid = (url?:string):boolean => {
    if(url) {
        return validateAndPullID(url) !== undefined;
    } else {
        return true;
    }
};

type GameSettingsProps = {
    common:CommonState
    sendMessage:(data:TriviaMessage) => void
}

function GameSettings({common, sendMessage} : GameSettingsProps) {
    const [options, setOptions] = useImmer(common.gameOptions);
    const [youtubeValid, setYoutubeValid] = useState(isYoutubeURLValid(common.gameOptions.streamUrl));

    useEffect(() => {
        setOptions(draft => {
            Object.assign(draft, common.gameOptions);
        });
    }, [setOptions,common.gameOptions]);

    const handleChange = (name: keyof Omit<GameOptions, "gameMode">) => (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const value = getInputValue(event.target);
        if(name === "streamUrl") {
            setYoutubeValid(isYoutubeURLValid(value));
        }
        setOptions(draft => {
            // Casting to never.
            // TS seems to have an issue with bools in operations like this: https://github.com/microsoft/TypeScript/issues/31663
            draft[name] = value as never;
            sendMessage({type:TriviaAction.Options, gameOptions:draft});
        });
    };

    return (
        <>
            <Switch name="showLeaderboard" labelText="Show Leaderboard"
                    value={options.showLeaderboard} labelVariant={TextVariant.s16reg}
                    onChange={handleChange("showLeaderboard")} reverse={true}/>
            <Switch name="showChat" labelText="Show Chat"
                    value={options.showChat} labelVariant={TextVariant.s16reg}
                    onChange={handleChange("showChat")} reverse={true}/>
            <Switch name="selfTimed" labelText="Self Timed - Remove timers and advance state manually."
                    value={options.selfTimed} labelVariant={TextVariant.s16reg}
                    onChange={handleChange("selfTimed")} reverse={true}/>
            <Switch name="backgroundMusic" labelText="Background music"
                    value={options.backgroundMusic} labelVariant={TextVariant.s16reg}
                    onChange={handleChange("backgroundMusic")} reverse={true}/>
            <Switch name="soundEffects" labelText="Sound Effects"
                    value={options.soundEffects} labelVariant={TextVariant.s16reg}
                    onChange={handleChange("soundEffects")} reverse={true}/>
            <Switch name="introVideoRectangle" labelText="Rectangular Intro Video"
                    value={options.introVideoRectangle} labelVariant={TextVariant.s16reg}
                    onChange={handleChange("introVideoRectangle")} reverse={true}/>
            <TextField labelText="YouTube Embed URL"
                name="streamUrl"
                value={options.streamUrl}
                errorText={!youtubeValid ? "Please enter a valid youtube URL." : undefined}
                onChange={handleChange("streamUrl")}
                helperText="YouTube embed URLs are ignored for Live events. Use this if you want to embed a live stream of a host."
                variant="filled" backgroundColor="#D2DAE0" backgroundHoverColor="#B4C1CB"
            />
            <TextField labelText="Host Name"
                name="hostName"
                value={options.hostName}
                onChange={handleChange("hostName")}
                helperText="The host name that shows on above the host video."
                variant="filled" backgroundColor="#D2DAE0" backgroundHoverColor="#B4C1CB"
            />
        </>
    );
}